import React, { useEffect, useState } from 'react'
import { FieldErrors, useWatch } from 'react-hook-form'
import get from 'lodash/get'
import {
  ControlledHiddenInput,
  ControlledSelect,
  ControlledTextField,
} from 'transfix-form/components'
import { INLINE_MESSAGE_VARIANTS, InlineMessage } from 'transfix-ui/components/InlineMessage'
import { Link, Typography, makeStyles } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { IVendor, Maybe } from 'types/graphqlTypes'
import ControlledVendorAutocomplete from 'apps/marketplace/components/ControlledVendorAutocomplete/ControlledVendorAutocomplete'
import { MIN_CHARACTERS } from 'apps/marketplace/pages/facilities/components/FacilityList/Keywords'
import ChipsTextInput from 'apps/marketplace/pages/facilities/components/FacilityList/components/ChipsTextInput'
import { VerifyKeywordDocument } from 'apps/marketplace/pages/facilities/components/FacilityList/graphql/VerifyKeyword'
import { industryOptions } from 'apps/marketplace/pages/facilities/components/FacilityVendors/constants'
import { useVendorQuery } from 'apps/marketplace/pages/facilities/components/FacilityVendors/graphql/Vendor'
import { SelectedFacilityAddress } from 'apps/marketplace/pages/facilities/components/MergeFacilitiesModals/components/SelectedFacilityAddress'
import { TSelectedFacility } from 'apps/marketplace/pages/facilities/components/MergeFacilitiesModals/components/constants'
import { useImperativeQuery } from 'utils/hooks'
import { TVendorSchema } from './VendorFormSchema'
import { useVendorFormContext } from './useVendorFormContext'

const getErrorMessage = <T extends FieldErrors<TVendorSchema>, K extends keyof T>(
  errors: T,
  keyName: K
): string | undefined => {
  return get(errors, [keyName, 'message'])
}

const useAddNewVendorModalStyles = makeStyles(theme => ({
  overview: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(1.5),
    padding: theme.spacing(2, 0),
    borderBottom: `1px solid ${brandColors.coolGray3}`,
    marginBottom: theme.spacing(2),
  },
  keywordMessage: {
    margin: theme.spacing(2, 0),
  },
  messageContainer: {
    padding: theme.spacing(2, 0),
    '& > div:not(:first-child)': {
      marginTop: theme.spacing(1),
    },
  },
  infoMessageTitle: {
    fontWeight: 600,
  },
  infoMessage: {
    marginBottom: theme.spacing(2),
  },
  selectedFacility: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    borderTop: `${brandColors.coolGray3} solid 1px`,
    paddingTop: theme.spacing(2),
  },
}))

export interface IVendorFormProps {
  mutationError?: string | null
  facility: Maybe<TSelectedFacility>
}

export const VendorForm = ({ facility, mutationError }: IVendorFormProps) => {
  const styles = useAddNewVendorModalStyles()
  const defaultKeywords = useWatch<string[]>({ name: 'vendorKeywords' })
  const [keywords, setKeywords] = useState<string[]>(defaultKeywords || [])
  const [selectedVendorId, setSelectedVendorId] = useState<string>('')
  const [vendorTaken, setVendorTaken] = useState<Pick<IVendor, 'id' | 'name'> | null>(null)
  const vendorId = useWatch<string>({ name: 'vendorId' })
  const { data: selectedVendorData } = useVendorQuery({
    variables: {
      id: vendorId || '',
    },
    skip: !vendorId,
  })

  const verifyKeyword = useImperativeQuery(VerifyKeywordDocument)
  const { control, errors, setValue } = useVendorFormContext()

  const validateNewInputHandler = async (input: string): Promise<[boolean, Error | null]> => {
    if (input.length < MIN_CHARACTERS) {
      return [false, new Error(`Keyword should be at least ${MIN_CHARACTERS} characters.`)]
    }

    const { data } = await verifyKeyword({
      keyword: input,
    })
    const { searchVendorForKeyword } = data || {}
    const { vendor } = searchVendorForKeyword || {}

    if (vendor) {
      setVendorTaken(vendor)
      return [false, new Error('This keyword is taken. Try another.')]
    }
    return [true, null]
  }

  useEffect(() => {
    setValue('vendorKeywords', keywords)
  }, [keywords, setValue])

  // Autocomplete vendor information from selected vendor on form
  useEffect(() => {
    if (selectedVendorId) {
      setValue('industry', selectedVendorData?.vendor?.industry || '')
    }
  }, [setValue, selectedVendorData, selectedVendorId])

  return (
    <form data-test='VendorForm'>
      {mutationError && (
        <InlineMessage type={INLINE_MESSAGE_VARIANTS.ALERT} message={mutationError} />
      )}
      <Typography variant='subtitle1'>Vendor Overview</Typography>
      <div className={styles.overview}>
        <div>
          <ControlledVendorAutocomplete
            name='name'
            control={control}
            VendorAutocompleteProps={{
              AutocompleteProps: {
                label: 'Vendor Name',
                placeholder: 'Vendor Name',
                required: true,
                inputError: !!getErrorMessage(errors, 'name'),
                inputValidationMessage: getErrorMessage(errors, 'name'),
                size: 'small',
              },
              dataTest: 'sd-add-vendor-name',
            }}
            onChange={(vendorId: string | null) => {
              if (vendorId) {
                setSelectedVendorId(vendorId)
                setValue('vendorId', vendorId)
              } else {
                setSelectedVendorId('')
                setValue('vendorId', '')
                setValue('industry', null)
              }
            }}
          />
        </div>
        <ControlledSelect
          control={control}
          name='industry'
          SelectProps={{
            label: 'Industry',
            options: industryOptions,
            error: !!getErrorMessage(errors, 'industry'),
            validationMessage: getErrorMessage(errors, 'industry'),
            placeholder: 'Industry',
            disabled: Boolean(selectedVendorData?.vendor?.industry && vendorId),
            dataTest: 'industry-input',
          }}
        />
        <ControlledTextField
          control={control}
          name='vendorFacilityAddressLine2'
          TextInputProps={{
            label: 'Suite, Dock, DC#, etc.',
            error: !!getErrorMessage(errors, 'vendorFacilityAddressLine2'),
            validationMessage: getErrorMessage(errors, 'vendorFacilityAddressLine2'),
            placeholder: 'Suite, Dock, DC#, etc.',
          }}
        />
      </div>
      {vendorId ? (
        <InlineMessage
          className={styles.infoMessage}
          message={
            <>
              <Typography className={styles.infoMessageTitle} variant='body2'>
                Unable to add keywords to existing vendors.
              </Typography>
              <Typography variant='body2'>
                Use the vendor detail view to manage vendor keywords.
              </Typography>
            </>
          }
        />
      ) : (
        <>
          <Typography variant='subtitle1'>Mapping Keywords</Typography>
          <Typography variant='body2' className={styles.keywordMessage}>
            Any keyword that matches a new incoming EDI Location Code or Shipper Facility Code name
            will automatically be mapped to this vendor. This will take effect when new shipments
            come. Keywords are not case sensitive and will match everything contained within the
            keyword phrase.
          </Typography>

          <ChipsTextInput
            TextFieldProps={{
              label: 'Keyword',
            }}
            buttonText='Add Keyword'
            startLabels={keywords}
            onChange={async newKeywords => setKeywords(newKeywords)}
            onInputChange={() => {
              setVendorTaken(null)
            }}
            validateNewInput={validateNewInputHandler}>
            <div className={styles.messageContainer}>
              {vendorTaken && (
                <InlineMessage
                  type={INLINE_MESSAGE_VARIANTS.WARNING}
                  message={
                    <span>
                      This keyword is being used by{' '}
                      <Link
                        href={`${__ROOT__}/facilities?vendorId=${vendorTaken.id}`}
                        target='_blank'
                        rel='noopener noreferrer'>
                        {vendorTaken.name}
                      </Link>
                    </span>
                  }
                />
              )}
            </div>
          </ChipsTextInput>
        </>
      )}
      <ControlledHiddenInput name='vendorKeywords' control={control} />
      <ControlledHiddenInput name='vendorId' control={control} />
      <div className={styles.selectedFacility}>
        <Typography variant='body2'>Merging facility into:</Typography>
        <SelectedFacilityAddress facility={facility} />
      </div>
    </form>
  )
}
